import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Form, Select, DatePicker, message } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import axios from "../../../helpers/Axios";

const { RangePicker } = DatePicker;
const dateFormat = "MM/DD/YYYY";

const ERMDOfficerReportCard = () => {
    const [dateRange, setDateRange] = useState([null, null]);
    const [filterGroups, setFilterGroups] = useState([]);
    const [ermdUsers, setErmdUsers] = useState([]);
    const [formTypes, setFormTypes] = useState([]);
    const [loadingReport, setLoadingReport] = useState(false);
    const [form] = Form.useForm();

    const statuses = [
        { id: 2, label: "For Review" },
        { id: 3, label: "Accepted" },
        { id: 4, label: "Reverted" },
        { id: 14, label: "Cancelled" },
        { id: 15, label: "Disputed" },
    ];

    useEffect(() => {
        axios.get("/api/ermd_users").then((res) => setErmdUsers(res.data));
        axios.get("/api/ost_data_resource/").then((res) => setFormTypes(res.data));
    }, []);

    const disabledDate = (current) => current && current > dayjs().endOf("day");

    const handleDateChange = (dates) => {
        setDateRange(dates);
    };

    const addFilterGroup = () => {
        const newGroup = `filterGroup_${filterGroups.length}`;
        setFilterGroups([...filterGroups, newGroup]);
    };

    const fields = [
        { value: 'receiving_officer', label: 'Receiving Officer' },
        { value: 'document_status', label: 'Document Status' },
        { value: 'form_type', label: 'Form Type' }
    ];

    const fieldOptions = {
        receiving_officer: ermdUsers.map(user => ({ value: user.id, label: user.name })),
        document_status: statuses.map(s => ({ value: s.id, label: s.label })),
        form_type: formTypes.map(f => ({ value: f.id, label: f.code }))
    };

    const handleGenerateReport = async (values) => {
        const { user_id, status, form_type } = values;
        const filters = values.filters || {};
        const filterParams = Object.values(filters).map(group => ({
            field: group.field,
            value: group.value,
        }));

        const [startDate, endDate] = dateRange;

        if (!startDate || !endDate) {
            return message.warning("Please select a valid date range.");
        }

        setLoadingReport(true);

        try {
            const response = await axios.get(
                `/api/reports/tracker/?start=${startDate.format("YYYY-MM-DD")}&end=${endDate.format(
                    "YYYY-MM-DD"
                )}&user=${user_id || ""}&status=${status || ""}&form_type=${form_type || ""}`,
                { responseType: "blob" }
            );
            fileDownload(
                response.data,
                `ERMD_Report_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`
            );
            message.success("Report generated successfully!");
            form.resetFields();
            setDateRange([null, null]);
        } catch (err) {
            console.error(err);
            message.error("Failed to generate report.");
        } finally {
            setLoadingReport(false);
        }
    };

    return (
        <Card title="Generate Receiving Officer Report">
            <Form layout="horizontal" form={form} onFinish={handleGenerateReport}>
                <Row gutter={16}>
                    <Col lg={12}>
                        <Form.Item
                            name="date_range"
                            label="Date Range"
                            rules={[{ required: true, message: "Please select date range" }]}
                        >
                            <RangePicker
                                style={{ width: "100%" }}
                                onChange={handleDateChange}
                                disabledDate={disabledDate}
                                format={dateFormat}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24} style={{ marginBottom: 24 }}>
                        <Button type="dashed" onClick={addFilterGroup} icon={<PlusOutlined />}>
                            Add Filter Group
                        </Button>
                    </Col>

                    {filterGroups.map((groupId, index) => (
                        <Col span={24} key={groupId}>
                            <Card 
                                size="small" 
                                style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    border: '1px solid #d9d9d9', 
                                    borderRadius: 6,
                                    marginBottom: 16
                                }}
                                bodyStyle={{ padding: 16 }}
                            >
                                <Row gutter={16}>
                                    <Col span={24} style={{ marginBottom: 12}}>
                                        <div style={{ fontSize: 16, fontWeight: 500 }}>Filter Group {index + 1}</div>
                                    </Col>

                                    <Col lg={10} md={12} sm={24}>
                                        <Form.Item name={['filters', groupId, 'field']} label="Field">
                                            <Select 
                                                placeholder="Select Field"
                                                onChange={(value) => {
                                                    const currentFilters = form.getFieldValue('filters') || {};
                                                    currentFilters[groupId] = {
                                                        ...currentFilters[groupId],
                                                        value: undefined
                                                    };
                                                    form.setFieldsValue({ filters: currentFilters });
                                                }}
                                            >
                                                {fields.map(field => (
                                                    <Select.Option key={field.value} value={field.value}>
                                                        {field.label}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col lg={10} md={12} sm={24}>
                                        <Form.Item shouldUpdate noStyle>
                                            {() => {
                                                const selectedField = form.getFieldValue(['filters', groupId, 'field']);
                                                const options = fieldOptions[selectedField] || [];
                                                return (
                                                    <Form.Item name={['filters', groupId, 'value']} label="Value">
                                                        <Select placeholder="Select Value" allowClear>
                                                            {options.map(opt => (
                                                                <Select.Option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </Col>

                                    <Col style={{ paddingTop: 4 }}>
                                        <Button
                                            danger
                                            onClick={() => setFilterGroups(filterGroups.filter(g => g !== groupId))}
                                        >
                                            Remove
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    ))}

                    {/* <Col xs={24} sm={24} md={24} lg={12}>
                        <Form.Item name="user_id" label="Receiving Officer">
                            <Select
                                placeholder="Select Officer"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                <Select.Option value="">All</Select.Option>
                                {ermdUsers.map((user) => (
                                    <Select.Option key={user.id} value={user.id}>
                                        {user.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={8}>
                        <Form.Item name="status" label="Document Status">
                            <Select
                                placeholder="Select Status"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                <Select.Option value="">All</Select.Option>
                                {statuses.map((status) => (
                                    <Select.Option key={status.id} value={status.id}>
                                        {status.label === "Submitted" ? "For Review" : status.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={8}>
                        <Form.Item name="form_type" label="Form Type">
                            <Select
                                placeholder="Select Form Type"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                <Select.Option value="">All</Select.Option>
                                {formTypes.map((a) => (
                                    <Select.Option key={a.id} value={a.id}>
                                        {a.code}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col> */}

                    <Col xs={24} sm={24} md={24} lg={8} style={{ textAlign: "left" }}>
                        <Button type="primary" htmlType="submit" loading={loadingReport}>
                            Generate Report
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};

export default ERMDOfficerReportCard;